import {Hex, Land} from '@shareland/shareland-common';
import {
  CollateralPosition__factory,
  LiquidityProviderToken__factory,
  OrderBook__factory,
  ShareLandMarket__factory,
  ShareLandToken__factory,
} from '@shareland/shareland-contracts';

import {wagmiConfig} from '#/wagmi/wagmi-config';
import {parseGwei} from 'viem';
import {readContract, writeContract} from 'wagmi/actions';

export class ShareLandMarketContract {
  account: Hex;
  land: Land;

  constructor(account: Hex, land: Land) {
    this.account = account;
    this.land = land;
  }

  async purchaseWithStableToken(stableTokenAmount: number, minReceivedLandTokenAmount: number) {
    const maxFeePerGas = parseGwei('41');
    const maxPriorityFeePerGas = parseGwei('39');
    return writeContract(wagmiConfig, {
      abi: ShareLandMarket__factory.abi,
      address: this.land.contractAddresses.market,
      functionName: 'purchaseWithMinReceived',
      args: [BigInt(stableTokenAmount), BigInt(minReceivedLandTokenAmount)],
      account: this.account,
      maxFeePerGas,
      maxPriorityFeePerGas,
    });
  }

  async sell(count: number, minReceivedStableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: ShareLandMarket__factory.abi,
      address: this.land.contractAddresses.market,
      functionName: 'sellWithMinReceived',
      args: [BigInt(count), BigInt(minReceivedStableTokenAmount)],
      account: this.account,
    });
  }

  async openCollateralPosition(landTokenAmount: number, stableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: CollateralPosition__factory.abi,
      address: this.land.contractAddresses.collateralPosition,
      functionName: 'openCollateralPosition',
      args: [BigInt(landTokenAmount), BigInt(stableTokenAmount)],
      account: this.account,
    });
  }

  async closeCollateralPosition(landTokenAmount: number, stableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: CollateralPosition__factory.abi,
      address: this.land.contractAddresses.collateralPosition,
      functionName: 'closeCollateralPosition',
      args: [BigInt(landTokenAmount), BigInt(stableTokenAmount)],
      account: this.account,
    });
  }

  async adjustCollateralPosition(landTokenAmount: number, stableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: CollateralPosition__factory.abi,
      address: this.land.contractAddresses.collateralPosition,
      functionName: 'adjustCollateralPosition',
      args: [BigInt(landTokenAmount), BigInt(stableTokenAmount)],
      account: this.account,
    });
  }

  async provideLiquidity(landTokenAmount: number, stableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: LiquidityProviderToken__factory.abi,
      address: this.land.contractAddresses.liquidityProviderToken,
      functionName: 'mintLiquidityProviderToken',
      args: [BigInt(landTokenAmount), BigInt(stableTokenAmount)],
      account: this.account,
    });
  }

  async burnLiquidityProviderToken(lpTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: LiquidityProviderToken__factory.abi,
      address: this.land.contractAddresses.liquidityProviderToken,
      functionName: 'burnLiquidityProviderToken',
      args: [BigInt(lpTokenAmount)],
      account: this.account,
    });
  }

  async claimRewards() {
    return writeContract(wagmiConfig, {
      abi: LiquidityProviderToken__factory.abi,
      address: this.land.contractAddresses.liquidityProviderToken,
      functionName: 'claimRewards',
      args: undefined,
      account: this.account,
    });
  }

  async createPurchaseOrder(landTokenAmount: number, stableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: OrderBook__factory.abi,
      address: this.land.contractAddresses.orderBook,
      functionName: 'createPurchaseOrder',
      args: [BigInt(landTokenAmount), BigInt(stableTokenAmount)],
      account: this.account,
    });
  }

  async createSellOrder(landTokenAmount: number, stableTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: OrderBook__factory.abi,
      address: this.land.contractAddresses.orderBook,
      functionName: 'createSellOrder',
      args: [BigInt(landTokenAmount), BigInt(stableTokenAmount)],
      account: this.account,
    });
  }

  async ordersByAccount(account: Hex) {
    return readContract(wagmiConfig, {
      abi: OrderBook__factory.abi,
      address: this.land.contractAddresses.orderBook,
      functionName: 'ordersByAccount',
      args: [account],
      account: this.account,
    });
  }

  async orderById(id: number) {
    return readContract(wagmiConfig, {
      abi: OrderBook__factory.abi,
      address: this.land.contractAddresses.orderBook,
      functionName: 'orderById',
      args: [BigInt(id)],
      account: this.account,
    });
  }

  async cancelOrder(id: number) {
    return writeContract(wagmiConfig, {
      abi: OrderBook__factory.abi,
      address: this.land.contractAddresses.orderBook,
      functionName: 'cancelOrder',
      args: [BigInt(id)],
      account: this.account,
    });
  }

  async approveLandTokenOrderBook(landTokenAmount: number) {
    return writeContract(wagmiConfig, {
      abi: ShareLandToken__factory.abi,
      address: this.land.contractAddresses.shareLandToken,
      functionName: 'approve',
      args: [this.land.contractAddresses.orderBook, BigInt(landTokenAmount)],
      account: this.account,
    });
  }

  async transferShareLandToken(to: Hex, amount: number) {
    return writeContract(wagmiConfig, {
      abi: ShareLandToken__factory.abi,
      address: this.land.contractAddresses.shareLandToken,
      functionName: 'transfer',
      args: [to, BigInt(amount)],
      account: this.account,
    });
  }

  async transferLiquidityProviderToken(to: Hex, amount: number) {
    return writeContract(wagmiConfig, {
      abi: LiquidityProviderToken__factory.abi,
      address: this.land.contractAddresses.liquidityProviderToken,
      functionName: 'transfer',
      args: [to, BigInt(amount)],
      account: this.account,
    });
  }
}
